import type { RefObject } from 'react'
import React, { useState, useCallback, useMemo } from 'react'
import {
  makeStyles,
  FormControlLabel,
  InputField,
  Editor,
  FormSection,
  Checkbox,
  useClientOptions
} from '@paintscout/ui'
import type { MuiTheme as Theme, EditorRef } from '@paintscout/ui'
import type { QuoteDocument } from 'paintscout'
import type { UpdateableLineItem, UpdateableProduct } from '@paintscout/util/builder'
import { getObjectLabels } from '@paintscout/util/builder'
import { makeHtml } from '@paintscout/util/util'
import MaterialsTable from '../../dialogs/EditAreaDialog/MaterialsTable'
import { useQuote } from '../../context'
import { calculateUpdateableLineItem } from './util'
import { LineItemPricing } from './LineItemPricing'

export interface LineItemFormProps {
  item: UpdateableLineItem
  quote: QuoteDocument
  hideName?: boolean
  showPricing?: boolean
  showNotes?: boolean
  showMaterials?: boolean
  autoFocus?: boolean
  settings?: boolean
  allowCustomProduct?: boolean
  onChange: (lineItem: UpdateableLineItem) => void
  descriptionRef: EditorRef
  crewNoteRef: EditorRef
  clientNoteRef: EditorRef
  nameRef: RefObject<HTMLInputElement>
}

const useStyles = makeStyles<Theme, LineItemFormProps>(
  (theme) => ({
    root: {},
    noteSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start'
    },
    nameSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: 0
    },
    nameInput: {
      '& input': {
        fontWeight: theme.typography.fontWeightMedium
      }
    },
    descriptionSection: {
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 0
    },
    priceSection: {
      position: 'relative'
    }
  }),
  { name: 'LineItemForm' }
)

function LineItemForm(props: LineItemFormProps) {
  const {
    hideName,
    showPricing,
    showNotes,
    showMaterials,
    quote,
    allowCustomProduct,
    autoFocus = true,
    settings = false,
    onChange
  } = props
  const classes = useStyles(props)
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const { isEditable, isNoteEditable } = useQuote()
  const initialItem = {
    price: 0,
    hours: '',
    calculate: false,
    customHourlyRate: false,
    hourlyRate: 0,
    ...props.item
  } as UpdateableLineItem
  const isSmartTemplate = quote?.type === 'quoteTemplate' && quote?.smartTemplate
  const isNoteDisabled = !settings && (isSmartTemplate || !isNoteEditable)
  const disabled = !settings && (isSmartTemplate || !isEditable)

  const [item, setItem] = useState(initialItem)

  const initialCrewNote = makeHtml(item.crewNote, { replaceBreaks: true })
  const initialClientNote = makeHtml(item.clientNote, { replaceBreaks: true })

  const handleCheckboxChange = useCallback(
    (event: any, value: any) => {
      const { name } = event.target

      const updatedItem = {
        ...item,
        [name]: value
      }

      if (name === 'customHourlyRate') {
        updatedItem.hourlyRate = null
      }

      const calculatedItem = calculateUpdateableLineItem({ item: updatedItem, options, quote })

      setItem(calculatedItem)

      onChange(calculatedItem)
    },
    [item, props]
  )

  const handleUpdateMaterials = useCallback(
    (materials: UpdateableProduct[]) => {
      setItem((item) => {
        const updatedItem = {
          ...item,
          materials
        }

        const calculatedItem = calculateUpdateableLineItem({ item: updatedItem, options, quote })

        onChange(calculatedItem)
        return calculatedItem
      })
    },
    [setItem, options, quote, onChange, calculateUpdateableLineItem]
  )

  const handleChange = (lineItem: UpdateableLineItem) => {
    setItem(lineItem)
    onChange(lineItem)
  }

  const MemoizedLineItemPricing = useMemo(
    () => LineItemPricing({ ...props, item, initialItem, setItem: handleChange, disabled }),
    [props, item, initialItem]
  )

  return (
    <div className={classes.root}>
      {!hideName && (
        <FormSection classes={{ root: classes.nameSection }} hideDivider>
          <InputField
            fullWidth={true}
            label={'Name'}
            disabled={disabled}
            defaultValue={item.label}
            name={'label'}
            autoFocus={autoFocus}
            classes={{ root: classes.nameInput }}
            inputRef={props.nameRef}
          />
        </FormSection>
      )}
      <FormSection classes={{ root: classes.descriptionSection }} hideDivider>
        <Editor
          label={'Description'}
          disabled={disabled}
          content={item.description}
          ref={props.descriptionRef}
          fullWidth={true}
        />
        <FormControlLabel
          control={
            <Checkbox
              disabled={disabled}
              checked={item.hideOnWorkOrder}
              onChange={handleCheckboxChange}
              name={'hideOnWorkOrder'}
            />
          }
          label={`Hidden on ${objectLabels.workOrder.value}`}
        />
      </FormSection>

      <div className={'showPricing'}>
        {showPricing && (
          <FormSection classes={{ root: classes.priceSection }} style={{ paddingBottom: showMaterials ? 16 : 0 }}>
            {MemoizedLineItemPricing}
          </FormSection>
        )}
      </div>

      {showNotes && (
        <div className={'showNotes'}>
          <FormSection classes={{ root: classes.noteSection }}>
            <Editor
              disabled={isNoteDisabled}
              label={'Crew Note'}
              fullWidth={true}
              content={initialCrewNote}
              ref={props.crewNoteRef}
            />
          </FormSection>
          <FormSection classes={{ root: classes.noteSection }} hideDivider>
            <Editor
              disabled={disabled}
              label={'Client Note'}
              fullWidth={true}
              content={initialClientNote}
              ref={props.clientNoteRef}
            />
          </FormSection>
        </div>
      )}

      <div className={'showMaterials'}>
        {showMaterials && (
          <MaterialsTable
            allowCustomProduct={allowCustomProduct}
            quoteTypes={quote?.int_ext?.value ? [quote?.int_ext?.value] : null}
            materials={item.materials}
            onChange={handleUpdateMaterials}
            options={options}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  )
}

export default LineItemForm
